import { Component, OnInit, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { AppSettings } from '@services/settings/app-settings.service';
import { CommonModule, Location } from '@angular/common';
import { HeaderComponent } from './core/components/header/header.component';
import { BottomNavComponent } from './core/components/bottom-nav/bottom-nav.component';
import { SidebarComponent } from './core/components/sidebar/sidebar.component';
import { MicrosoftAuthService } from '@services/core/authentication.service';
import { AuthService } from '@services/core/auth.service';
import { StorageService } from '@services/core/storage.service';
import Swal from 'sweetalert2'
import { AppVariablesService } from '@services/app-variables.service';
import { ThemePanelComponent } from './core/components/theme-panel/theme-panel.component';
import { LoginComponent } from './core/components/login/login.component';
import { CatalogService } from './core/service/data/catalog.service';
import { NotificationHubService } from './core/service/core/notification-hub.service';
import { PeriodService } from './feature/management/billing-management/services/period.service';
import { SocketService } from './core/service/core/socket.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, HeaderComponent, BottomNavComponent, SidebarComponent, ThemePanelComponent, LoginComponent, CommonModule],
  providers: [],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  code = input<any>();
  title = 'pres.Ui';
  isLogged
  sessionHold = false
  showLogin = false
  private _sessionListenerRegistered = false;
  private _promptOpen = false;
  authMicrosoft = inject(MicrosoftAuthService)
  activatedRoute = inject(ActivatedRoute)
  authService = inject(AuthService)
  location = inject(Location)
  router = inject(Router)
  storageService = inject(StorageService)
  catalogService = inject(CatalogService)
  private notificationHub = inject(NotificationHubService)
  private periodService = inject(PeriodService)
  private socketService = inject(SocketService)
  private _remoteSession = false
  private _restoreTried = false
  swalBackground
  swalTextColor
  constructor(private appVariablesService: AppVariablesService,
    public appSettings: AppSettings
  ) { }

  appVariables = this.appVariablesService.getAppVariables()



  ngOnInit() {
    const hold = sessionStorage.getItem('fpv_hold_login') === '1';
    const logged = !!this.storageService.getJsonValue('logged');
    if (hold && logged) sessionStorage.removeItem('fpv_hold_login');
    if (hold && !logged) {
      this.sessionHold = true;
      this.isLogged = false;
      this.initStyleSettings();
      this._listenBrowserSession();
      this.authService.signedOut$.subscribe(() => this._showLoginPanel());
      return;
    }
    this.isLogged = logged;
    if (!this.isLogged) {
      this.handleNotLoggedIn();
    } else {
      this.handleLoggedIn();
      // Escuchar expiración solo si ya estaba logueado
      this._listenSessionExpired();
    }

    this.initStyleSettings();
    this._listenBrowserSession();
    this.authService.signedOut$.subscribe(() => this._showLoginPanel());
  }

  private _listenSessionExpired() {
    this.authService.sessionExpired$.subscribe(() => {
      if (this.isLogged) {
        this.showCautionSesion();
      }
    });
  }

  get isPublicPage(): boolean {
    return this.location.path().includes('/confirm-request');
  }

  private handleNotLoggedIn() {
    if (this.isPublicPage) return;
    this.activatedRoute.queryParams.subscribe((params) => {
      // Si ya tenemos un proceso de login en marcha o ya estamos logueados, no redirigimos
      if (this.isLogged) return;

      if (params["code"]) {
        this.showLogin = false;
        this.getToken(params["code"]);
      } else if (!this.location.path().includes('code=')) {
        this.showLogin = true;
      }
    });
  }

  private handleLoggedIn() {
    const path = this.location.path().includes("?code") ? "" : this.location.path();
    this.router.navigateByUrl(path);
    this.getUserData();
  }


  private initStyleSettings() {
    this.initThemeSettings();
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', () => {
      if (this.appSettings.explicitDarkMode() === null) this.onAppDarkModeChanged(true);
    });
  }


  cleanUrl() {
    const path = this.location.path().split('?')[0];
    this.router.navigateByUrl(path, { replaceUrl: true });
  }

  initThemeSettings() {
    if (localStorage['appGradientEnabled']) {
      this.appSettings.appGradientEnabled = localStorage['appGradientEnabled'] === 'true';
    }
    this.onAppDarkModeChanged(true);
  }

  // set page minified
  onAppSidebarMinifiedToggled(val): void {
    this.appSettings.appSidebarMinified = !this.appSettings.appSidebarMinified;
    if (localStorage) {
      localStorage['appSidebarMinified'] = this.appSettings.appSidebarMinified;
    }
  }

  onAppDarkModeChanged(val: boolean): void {
    const dark = this.appSettings.resolvedDarkMode();
    this.appSettings.appDarkMode = dark;
    document.documentElement.setAttribute('data-bs-theme', dark ? 'dark' : 'light');
    this.appSettings.appGradientEnabled = (localStorage['appGradientEnabled'] === 'true') ? true : false;
    this.appVariables = this.appVariablesService.getAppVariables();
    this.appVariablesService.variablesReload.emit();
    document.dispatchEvent(new CustomEvent('theme-change'));
  }

  // hide mobile sidebar
  onAppSidebarMobileToggled(val): void {
    this.appSettings.appSidebarMobileToggled = !this.appSettings.appSidebarMobileToggled;
  }

  loginMicrosoft() {
    const askCredentials = sessionStorage.getItem('fpv_hold_login') === '1';
    sessionStorage.removeItem('fpv_hold_login');
    this.authMicrosoft.getTokenAPI().subscribe(res => {
      window.location.href = askCredentials ? this._withLoginPrompt(res?.url) : res.url;
    });
  }

  private _withLoginPrompt(url: string): string {
    if (!url) return url;
    if (/[?&]prompt=/.test(url)) return url;
    return url + (url.includes('?') ? '&' : '?') + 'prompt=login';
  }

  getToken(code: string) {
    this.authMicrosoft.getAccessToken(code).subscribe({
      next: (res) => this._handleTokenSuccess(res),
      error: (error) => console.error('Error al obtener el token:', error)
    });
  }

  private _handleTokenSuccess(res: any) {
    this.authMicrosoft.token.set(res.access_token);
    this.authMicrosoft.refreshToken.set(res.refresh_token);
    this.isLogged = true;
    this.storageService.setJsonValue("logged", "true");
    this.storageService.setJsonValue("token", res.access_token);
    this.storageService.setJsonValue("refresh_token", res.refresh_token);
    this.cleanUrl();
    this.getUserData(true);
  }

  getUserData(reloadPhoto = false) {
    this.swalDarkMode();
    this._showLoadingSwal();
    this.authMicrosoft.decryptToken(reloadPhoto).subscribe({
      next: (res) => {
        if (reloadPhoto) this.authMicrosoft.dropMicrosoftToken();
        this._handleAuthResponse(res);
      },
      error: (err) => this._handleAuthError(err)
    });
  }

  private _showLoadingSwal() {
    Swal.fire({
      allowOutsideClick: false,
      showConfirmButton: false,
      background: this.swalBackground,
      color: this.swalTextColor,
      willOpen: () => Swal.showLoading()
    });
  }

  private _handleAuthResponse(res: any) {
    if (res.error) return this._showAccessDenied(res.error);
    this._processValidSession(res);
  }

  private _handleAuthError(err: any): void {
    Swal.close();
    if (err?.error?.code === 'NO_SESSION') {
      this._restoreOrShowLogin();
      return;
    }
    console.error("Error decrypting token:", err);
    const isAuthError = err?.status === 401 || err?.status === 403;
    if (isAuthError) this.showCautionSesion();
  }

  private _restoreOrShowLogin(): void {
    if (this._restoreTried) {
      this._showLoginPanel();
      return;
    }
    this._restoreTried = true;
    this.authMicrosoft.refreshFpvSession().subscribe({
      next: () => this.getUserData(),
      error: () => this._showLoginPanel()
    });
  }

  private _showLoginPanel(): void {
    this.authMicrosoft.clearSesion();
    this.isLogged = false;
    this.sessionHold = false;
    this.showLogin = true;
    this._promptOpen = false;
    Swal.close();
  }

  private _showAccessDenied(error: any) {
    if (error?.code === "InvalidAuthenticationToken") return this.showCautionSesion();

    Swal.fire({
      title: "Acceso Denegado",
      text: error,
      icon: "error",
      background: this.swalBackground,
      color: this.swalTextColor,
      confirmButtonText: "Salir",
      allowOutsideClick: false
    }).then(() => this._logoutAndRedirect());
  }

  private _processValidSession(res: any) {
    try {
      this._setAuthServiceState(res);
      this.isLogged = !!this.storageService.getJsonValue('logged');
      this.sessionHold = false;
      this.showLogin = false;
      this._registerSessionListener();
      this.catalogService.loadCatalogs();
      this.periodService.reload();
      if (!this._remoteSession) this.socketService.announceBrowserSession('login');
    } catch (error) {
      console.error('Error en el procesamiento de datos:', error);
    }
    this._remoteSession = false;
    Swal.close();
  }

  private _setAuthServiceState(res: any) {
    const user = res.user;
    this.authService.processUserData(user.user);
    if (res.photo?.body?.data) {
      this.authService.processUserPhoto(res.photo.body.data);
    }
    this.authService.sharingEntities.set(user.entities);
    this.authService.sharingEntity.set(user.entity);
    this.authService.sharingIdentity.set(user.identity);
    this.authService.sharingMenu.set(user.menu);
    this.storageService.setJsonValue('menu', user.menu);
    this.authService.sharingRole.set(user.role);
    this.storageService.setJsonValue('role', user.role);
    const roles = Array.isArray(user.roles) ? user.roles : (user.role ? [user.role] : []);
    this.authService.sharingRoles.set(roles);
    this.storageService.setJsonValue('roles', roles);
    const menusByRole = user.menusByRole && typeof user.menusByRole === 'object' ? user.menusByRole : {};
    this.authService.sharingMenusByRole.set(menusByRole);
    this.storageService.setJsonValue('menusByRole', menusByRole);
    const entitiesByRole = user.entitiesByRole && typeof user.entitiesByRole === 'object' ? user.entitiesByRole : {};
    this.authService.sharingEntitiesByRole.set(entitiesByRole);
    this.storageService.setJsonValue('entitiesByRole', entitiesByRole);
    if (user.role?.id != null && menusByRole[String(user.role.id)]) {
      this.authService.sharingMenu.set(menusByRole[String(user.role.id)]);
      this.storageService.setJsonValue('menu', menusByRole[String(user.role.id)]);
    }
  }

  private _registerSessionListener() {
    if (!this._sessionListenerRegistered) {
      this._sessionListenerRegistered = true;
      this._listenSessionExpired();
    }
  }


  swalDarkMode() {
    if (this.appSettings.resolvedDarkMode()) {
      this.swalBackground = '#333';
      this.swalTextColor = '#fff';
    }
    else {
      this.swalBackground = '#fff';
      this.swalTextColor = '#333';
    }
  }


  showCautionSesion(): void {
    if (this._promptOpen) return;
    this._promptOpen = true;
    this.swalDarkMode();
    Swal.fire(this._getCautionSwalConfig()).then(result => {
      this._promptOpen = false;
      if (result.isConfirmed) this._renewSession();
      else this._logoutAndRedirect();
    });
  }

  private _getCautionSwalConfig(): any {
    return {
      title: "Su sesión de FPV ha expirado",
      icon: "error",
      showCancelButton: true,
      confirmButtonText: "Iniciar sesión",
      cancelButtonText: "Cerrar sesión",
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "red",
      background: this.swalBackground,
      color: this.swalTextColor,
      allowOutsideClick: false
    };
  }

  private _renewSession(): void {
    this.authMicrosoft.refreshFpvSession().subscribe({
      next: () => this.getUserData(),
      error: () => {
        this.isLogged = false;
        this.showLogin = true;
      }
    });
  }

  private _logoutAndRedirect(): void {
    sessionStorage.setItem('fpv_hold_login', '1');
    this.authMicrosoft.logout().subscribe({
      next: () => this._showLoggedOut(),
      error: () => this._showLoggedOut()
    });
  }

  private _listenBrowserSession() {
    this.socketService.onBrowserSession().subscribe((action) => {
      if (action === 'logout') this._applyRemoteLogout();
      else if (action === 'login') this._applyRemoteLogin();
      else if (action === 'context' && this.isLogged) this.authService.refreshSessionAccess();
    });
  }

  private _applyRemoteLogout() {
    sessionStorage.setItem('fpv_hold_login', '1');
    this.authMicrosoft.clearSesion();
    this.isLogged = false;
    this.showLogin = true;
    this.sessionHold = true;
    this._promptOpen = false;
    Swal.close();
  }

  private _applyRemoteLogin() {
    if (this.isLogged) return;
    this._remoteSession = true;
    sessionStorage.removeItem('fpv_hold_login');
    this.sessionHold = false;
    this.getUserData();
  }

  private _showLoggedOut(): void {
    this.socketService.announceBrowserSession('logout');
    this.authMicrosoft.clearSesion();
    this.isLogged = false;
    this.sessionHold = true;
    Swal.close();
  }


  onAppThemeChanged(val: boolean): void {
    const newTheme = 'theme-' + this.appSettings.appTheme;
    for (let x = 0; x < document.body.classList.length; x++) {
      if ((document.body.classList[x]).indexOf('theme-') > -1 && document.body.classList[x] !== newTheme) {
        document.body.classList.remove(document.body.classList[x]);
      }
    }
    document.body.classList.add(newTheme);
    this.appVariables = this.appVariablesService.getAppVariables();
    this.appVariablesService.variablesReload.emit();
  }



}
